import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { X, Minus, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { normaliseRegion } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// working_basket_29oct2026 (CEO mockup product_page.html): slide-out right
// panel on desktop, full screen on mobile. Order / Quote tabs.
export const BasketPanel = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const {
    items, removeItem, updateQuantity, clearCart,
    quoteItems, removeQuoteItem, updateQuoteQty, clearQuote,
    basketOpen, basketTab, setBasketTab, closeBasket,
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [sentQuote, setSentQuote] = useState(null);
  const [dup, setDup] = useState(false);

  const region = normaliseRegion(user?.region || 'uk') === 'nigeria' ? 'nigeria' : 'uk';
  const isNigeria = region === 'nigeria';
  const money = (n) => isNigeria ? `₦${Math.round(Number(n || 0)).toLocaleString('en-GB')}` : `£${Number(n || 0).toFixed(2)}`;

  if (!basketOpen) return null;

  const list = basketTab === 'quote' ? quoteItems : items;
  const subtotal = list.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const vat = isNigeria ? subtotal * 0.075 : subtotal * 0.2 + (basketTab === 'order' && list.length ? 10 / 1.2 * 0.2 : 0);
  const delivery = isNigeria ? 0 : (basketTab === 'order' && list.length ? 10 / 1.2 : 0);
  const grand = subtotal + vat + delivery;
  const count = list.reduce((s, i) => s + i.quantity, 0);

  const placeOrder = async (allowDuplicate = false) => {
    if (!list.length || submitting) return;
    setSubmitting(true);
    try {
      const orderData = {
        items: items.map(i => ({
          product_id: i.product_id, product_name: i.product_name, size: i.size,
          quantity: i.quantity, unit_price: i.unit_price,
          total_price: i.unit_price * i.quantity,
          pack_id: i.pack_id || null, pack_name: i.pack_name || null,
        })),
        delivery_method: 'standard_delivery', payment_method: 'bank_transfer',
        notes: null, allow_duplicate: allowDuplicate,
      };
      if (region === 'uk') {
        const { data } = await axios.post(`${API}/cart/checkout-v2`, orderData, { headers: { Authorization: `Bearer ${token}` } });
        setPlaced({ ref: data.order_ref, total: data?.totals?.total_inc_vat ?? grand });
      } else {
        const { data } = await axios.post(`${API}/orders`, orderData, { headers: { Authorization: `Bearer ${token}` } });
        setPlaced({ ref: data?.order_ref || data?.order_id || 'Order placed', total: grand });
      }
      setDup(false);
      clearCart();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.reason === 'duplicate_order_suspected') { setDup(true); }
      else toast.error(typeof detail === 'string' ? detail : 'Could not place the order');
    } finally { setSubmitting(false); }
  };

  const sendQuote = async () => {
    if (!quoteItems.length || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/uk-quotes/create`, {
        items: quoteItems.map(i => ({
          item_id: `${i.product_id}_${i.size}`,
          product_id: i.product_id, product_name: i.product_name, size: i.size,
          quantity: i.quantity, price: i.unit_price, unit_price: i.unit_price,
        })),
        region, notes: 'Requested from the basket',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSentQuote({ ref: data?.quote_number || data?.quote?.quote_number || 'Quote sent' });
      clearQuote();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send the quote');
    } finally { setSubmitting(false); }
  };

  const Line = ({ i, quote }) => (
    <div className="flex gap-3 py-3 border-b border-[#E8DDD5]" data-testid={`basket-line-${i.key}`}>
      <div className="w-[72px] h-[72px] shrink-0 grid place-items-center rounded-lg" style={{ background: '#F6F0EA' }}>
        {i.image_url ? <img src={i.image_url} alt="" className="max-w-[84%] max-h-[64px] object-contain" style={{ mixBlendMode: 'multiply' }} /> : <span className="font-serif text-xl text-[#B99A65]">{(i.product_name || 'R')[0]}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[15px] leading-snug">{i.product_name}</div>
        <div className="text-[13px] text-[#64525D]">{i.size} · {money(i.unit_price)}</div>
        <div className="inline-flex items-center gap-2.5 mt-2">
          <button data-testid={`basket-minus-${i.key}`} onClick={() => quote ? updateQuoteQty(i.key, i.quantity - 1) : updateQuantity(i.key, i.quantity - 1)}
            className="w-[34px] h-[34px] rounded-full border border-[#E8DDD5] bg-white text-lg leading-none"><Minus className="w-4 h-4 mx-auto" /></button>
          <b>{i.quantity}</b>
          <button data-testid={`basket-plus-${i.key}`} onClick={() => quote ? updateQuoteQty(i.key, i.quantity + 1) : updateQuantity(i.key, i.quantity + 1)}
            className="w-[34px] h-[34px] rounded-full border border-[#E8DDD5] bg-white text-lg leading-none"><Plus className="w-4 h-4 mx-auto" /></button>
          <button data-testid={`basket-remove-${i.key}`} onClick={() => quote ? removeQuoteItem(i.key) : removeItem(i.key)}
            className="text-[13px] text-[#64525D] underline ml-1">Remove</button>
        </div>
      </div>
      <div className="text-right font-semibold text-[15px]">{money(i.unit_price * i.quantity)}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" style={{ background: 'rgba(41,21,37,.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) closeBasket(); }} data-testid="basket-overlay">
      <div className="h-full w-full sm:w-[min(560px,100%)] overflow-auto flex flex-col"
        style={{ background: '#F8F4F1', color: '#2B1F27', fontFamily: "Lexend,'Helvetica Neue',Arial,sans-serif" }} data-testid="basket-panel">
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4" style={{ background: '#291525', color: '#F8F4F1' }}>
          <div className="font-serif text-xl" style={{ fontFamily: "'Bodoni Moda',Georgia,serif" }}>Your basket</div>
          <button onClick={closeBasket} data-testid="basket-close-btn" className="w-10 h-10 grid place-items-center rounded-full border" style={{ borderColor: 'rgba(217,190,142,.5)', color: '#D9BE8E' }}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 px-5 pt-4">
          {['order', 'quote'].map(t => (
            <button key={t} onClick={() => { setBasketTab(t); setPlaced(null); setSentQuote(null); setDup(false); }}
              data-testid={`basket-tab-${t}`}
              className="h-10 px-5 rounded-full border font-medium capitalize"
              style={basketTab === t ? { background: '#291525', color: '#F8F4F1', borderColor: '#291525' } : { borderColor: '#DCCFC6', background: '#fff' }}>
              {t} {t === 'order' ? `(${items.reduce((s, i) => s + i.quantity, 0)})` : `(${quoteItems.reduce((s, i) => s + i.quantity, 0)})`}
            </button>
          ))}
        </div>

        <div className="flex-1 px-5 py-4">
          {placed && basketTab === 'order' ? (
            <div className="text-center py-10" data-testid="order-confirmation">
              <span className="mx-auto w-[62px] h-[62px] rounded-full grid place-items-center" style={{ background: '#291525', color: '#D9BE8E' }}><Check className="w-8 h-8" /></span>
              <h3 className="font-serif text-2xl mt-3" style={{ fontFamily: "'Bodoni Moda',Georgia,serif" }}>Order placed</h3>
              <p className="text-[#64525D] mt-1">{placed.ref} · {money(placed.total)}</p>
              <p className="text-sm text-[#64525D] mt-2">{isNigeria ? 'We will confirm delivery before we send. Your invoice follows with payment details.' : 'Your invoice follows by email with the bank details.'}</p>
              <button onClick={() => { closeBasket(); navigate('/dashboard/my-salon-orders'); }} data-testid="view-order-btn"
                className="mt-5 min-h-[48px] px-6 rounded-full font-semibold" style={{ background: '#B99A65', color: '#2B1F27' }}>View order</button>
            </div>
          ) : sentQuote && basketTab === 'quote' ? (
            <div className="text-center py-10" data-testid="quote-confirmation">
              <span className="mx-auto w-[62px] h-[62px] rounded-full grid place-items-center" style={{ background: '#291525', color: '#D9BE8E' }}><Check className="w-8 h-8" /></span>
              <h3 className="font-serif text-2xl mt-3" style={{ fontFamily: "'Bodoni Moda',Georgia,serif" }}>Quote on its way</h3>
              <p className="text-[#64525D] mt-1">{sentQuote.ref}</p>
              <button onClick={() => { closeBasket(); navigate('/dashboard/my-salon-quotes'); }} data-testid="view-quote-btn"
                className="mt-5 min-h-[48px] px-6 rounded-full font-semibold" style={{ background: '#B99A65', color: '#2B1F27' }}>View quote</button>
            </div>
          ) : list.length === 0 ? (
            <p className="text-[#64525D] py-10 text-center" data-testid="basket-empty">Your {basketTab} is empty.</p>
          ) : (
            <>
              {list.map(i => <Line key={i.key} i={i} quote={basketTab === 'quote'} />)}
              <div className="max-w-[420px] ml-auto mt-4 text-[15px]" data-testid="basket-totals">
                <div className="flex justify-between py-1.5"><span>Subtotal{isNigeria ? '' : ' (ex VAT)'}</span><span>{money(subtotal)}</span></div>
                <div className="flex justify-between py-1.5"><span>VAT ({isNigeria ? '7.5%' : '20%'})</span><span>{money(vat)}</span></div>
                {basketTab === 'order' && (
                  <div className="flex justify-between py-1.5"><span>Delivery</span><span>{isNigeria ? 'Confirmed before we send' : `${money(delivery)} (£10.00 inc VAT)`}</span></div>
                )}
                <div className="flex justify-between pt-3 mt-1.5 text-[22px]" style={{ borderTop: '2px solid #B99A65', fontFamily: "'Bodoni Moda',Georgia,serif" }}>
                  <span>{basketTab === 'quote' ? 'Quote total' : 'Grand total'}</span><span data-testid="basket-grand-total">{money(basketTab === 'quote' ? subtotal + vat : grand)}</span>
                </div>
              </div>
              <p className="text-[13px] text-[#64525D] mt-3" data-testid="basket-note">
                {isNigeria
                  ? 'Prices in naira. VAT 7.5%. Delivery is confirmed before we send — your invoice follows with the payment details.'
                  : 'Delivery £10.00 inc VAT. Your invoice follows by email with the bank details.'}
              </p>
              {dup && basketTab === 'order' && (
                <div className="mt-3 rounded-xl p-3 text-sm" style={{ background: '#FBE8D7', color: '#9A4A0E' }} data-testid="duplicate-warning">
                  This looks like a duplicate of a recent order.
                  <button onClick={() => placeOrder(true)} className="underline font-semibold ml-2">Place it anyway</button>
                </div>
              )}
            </>
          )}
        </div>

        {list.length > 0 && !placed && !sentQuote && (
          <div className="sticky bottom-0 px-5 py-4 bg-white border-t border-[#E8DDD5]">
            <div className="flex justify-between items-baseline mb-2.5 text-[15px]">
              <span>{count} item{count === 1 ? '' : 's'}</span>
              <b className="text-[22px] text-[#7E5F2E]" style={{ fontFamily: "'Bodoni Moda',Georgia,serif" }}>{money(basketTab === 'quote' ? subtotal + vat : grand)}</b>
            </div>
            {basketTab === 'order' ? (
              <button onClick={() => placeOrder(false)} disabled={submitting} data-testid="place-order-btn"
                className="w-full min-h-[52px] rounded-full font-semibold tracking-wide disabled:opacity-50" style={{ background: '#B99A65', color: '#291525' }}>
                {submitting ? 'Placing…' : 'Place order'}
              </button>
            ) : (
              <button onClick={sendQuote} disabled={submitting} data-testid="send-quote-btn"
                className="w-full min-h-[52px] rounded-full font-semibold tracking-wide disabled:opacity-50" style={{ background: '#B99A65', color: '#291525' }}>
                {submitting ? 'Sending…' : 'Send me this quote'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasketPanel;
